/** «Спросить»: свободный вопрос по своим деньгам (AI-PRINCIPLES §5, §6).
 *
 * Ответ печатается по мере генерации, а пока модель ходит за данными — видно,
 * КУДА именно она полезла. Крутилка сообщает «жди», а это сообщает, что
 * происходит: с инструментами до первого слова проходит до десятка секунд.
 *
 * Запасной путь обязателен: не осилил клиент чтение потока — молча повторяем
 * вопрос обычным запросом. Ассистент, показывающий пустоту, хуже медленного.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.ts";
import { TAB_BAR_HEIGHT } from "../components/TabBar.tsx";
import { AnswerChart } from "../components/AnswerChart.tsx";
import { splitAnswer } from "../lib/answerBlocks.ts";
import { SpendSheet } from "../components/SpendSheet.tsx";
import { initData } from "../lib/telegram.ts";
import { tap } from "../lib/telegram.ts";

type Turn = {
  me: boolean;
  text: string;
  busy?: string | null;
  /** Что именно прочитано ради этого ответа. Приходит в финальном событии.
   *
   *  Показывать ОБЯЗАТЕЛЬНО. Сервер шлёт эту строку с самого начала, но экран её
   *  выбрасывал — и зря: 08.09 модель не знала сегодняшней даты, запросила август
   *  2023-го, получила пустую выборку и уверенно ответила «расходов не было».
   *  Ложь выдала ровно она — «прочитано: 0 операций · 2023-08-01». Без неё число
   *  в ответе неотличимо от выдуманного. */
  sources?: string[];
};

/** Что показываем, пока модель смотрит данные. Имя инструмента человеку
 *  ничего не говорит — переводим. */
const SAYS: Record<string, string> = {
  spending_summary: "смотрю траты за период",
  find_operations: "ищу операции",
  list_subscriptions: "смотрю подписки",
  last_capital: "смотрю капитал",
};

const SUGGESTS = [
  "Куда уходят деньги в этом месяце?",
  "Сколько я могу тратить до конца месяца?",
  "Что изменилось по сравнению с прошлым месяцем?",
];

export function AskScreen({ onBack }: { onBack?: () => void }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState<string | null>(null);
  const [showSpend, setShowSpend] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => { bottom.current?.scrollIntoView({ block: "end" }); }, [turns]);

  // Бюджет показываем СРАЗУ, а не после первого вопроса: узнать про границу
  // заранее — весь смысл счётчика, а упереться в неё посреди разговора хуже.
  useEffect(() => {
    api.llmSpend().then((s) => setLeft(s.remaining)).catch(() => undefined);
  }, []);

  const ask = async (question: string) => {
    if (!question.trim() || busy) return;
    setBusy(true);
    setText("");
    // Пузырь ответа заводим сразу: «думаю» появляется до первого байта с сервера,
    // иначе экран стоит пустым всё время, пока модель думает.
    setTurns((prev) => [...prev, { me: true, text: question },
                        { me: false, text: "", busy: "думаю" }]);

    const patch = (fn: (t: Turn) => Turn) =>
      setTurns((prev) => prev.map((t, i) => (i === prev.length - 1 ? fn(t) : t)));

    try {
      const ok = await stream(question, patch, setLeft);
      if (!ok) {
        const r = await fetch("/api/ask", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Telegram-Init-Data": initData() },
          body: JSON.stringify({ question }),
        });
        if (!r.ok) throw new Error(String(r.status));
        const body = await r.json();
        patch(() => ({ me: false, text: body.text, busy: null, sources: body.sources }));
        setLeft(body.remaining);
      } else {
        patch((t) => ({ ...t, busy: null }));
      }
    } catch {
      patch((t) => ({
        me: false,
        text: t.text || "Не получилось спросить. Попробуй ещё раз.",
        busy: null,
      }));
    } finally {
      setBusy(false);
    }
  };

  return (
    // Место под закреплённое поле ввода: без него последнее сообщение уезжает
    // под него и читается наполовину. Высота поля плюс воздух — 76px, столько же
    // резервирует у себя мини-апп про питание.
    <div style={{ padding: "var(--screen)", paddingBottom: "76px" }}>
      {/* «Назад» только когда есть куда: с вкладки возвращаться некуда, и
          кнопка вела бы в никуда. */}
      {onBack && (
        <button type="button" onClick={() => { tap(); onBack(); }}
          style={{ background: "none", border: "none", color: "var(--accent)", font: "inherit",
                   padding: "6px 0", cursor: "pointer", minHeight: "var(--tap)" }}>
          ‹ Назад
        </button>
      )}
      <h1 style={{ fontSize: "var(--f-t2)", margin: "0 0 var(--s-2)" }}>Спросить</h1>
      <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                  margin: "0 0 var(--s-4)", lineHeight: 1.5 }}>
        Ассистент смотрит твои записи сам: операции, траты по периодам, подписки, капитал.
        Он не финансовый консультант и не даёт советов по вложениям.
      </p>
      {left !== null && (
        <button type="button" onClick={() => { tap(); setShowSpend(true); }}
          style={{ display: "flex", alignItems: "center", gap: 6, width: "100%",
                   background: "none", border: "none", padding: "0 0 var(--s-4)",
                   margin: 0, font: "inherit", fontSize: "var(--f-caption)",
                   color: "var(--text-faint)", cursor: "pointer", textAlign: "left" }}>
          <span className="num" style={{ flexGrow: 1 }}>
            Осталось бюджета на разбор: {Number(left).toFixed(2)} $
          </span>
          {/* Стрелка обязательна: строка со ссылочным поведением, ничем не
              помеченная, читается как подпись — и по ней не нажимают. */}
          <span style={{ color: "var(--accent)" }}>Куда ушло ›</span>
        </button>
      )}

      {turns.length === 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-2)",
                      marginBottom: "var(--s-4)" }}>
          {SUGGESTS.map((q) => (
            <button key={q} type="button" onClick={() => { tap(); void ask(q); }}
              style={{ background: "var(--surface)", border: "none", borderRadius: "var(--r-pill)",
                       padding: "9px 14px", font: "inherit", fontSize: "var(--f-caption)",
                       color: "var(--text)", cursor: "pointer", minHeight: "var(--tap)" }}>
              {q}
            </button>
          ))}
        </div>
      )}

      {turns.map((t, i) => (
        <div key={i} style={{
          background: t.me ? "var(--accent-tint)" : "var(--surface)",
          borderRadius: "var(--r-lg)", padding: "var(--s-3) var(--s-4)",
          marginTop: "var(--s-3)", marginLeft: t.me ? "var(--s-5)" : 0,
          marginRight: t.me ? 0 : "var(--s-5)",
          fontSize: "var(--f-body-s)", lineHeight: 1.55, whiteSpace: "pre-wrap",
        }}>
          {/* Ответ ассистента может нести график: модель присылает подписи и
              числа, рисует приложение. Разметку от неё не берём — в описаниях
              трат живёт текст, который ввёл человек, и вставлять его как HTML
              значило бы открыть инъекцию в мини-апп с подписью Telegram. */}
          {t.me
            ? t.text
            : splitAnswer(t.text).map((block, k) =>
                block.kind === "text"
                  ? <div key={k} style={{ whiteSpace: "pre-wrap" }}>{block.text}</div>
                  : <AnswerChart key={k} chart={block.chart} />,
              )}
          {!t.me && t.sources?.length ? (
            <div className="num" style={{ fontSize: "var(--f-micro)",
                                          color: "var(--text-faint)",
                                          marginTop: "var(--s-2)" }}>
              {t.sources.join(" · ")}
            </div>
          ) : null}
          {t.busy && (
            <span style={{ color: "var(--text-muted)", fontSize: "var(--f-caption)" }}>
              {t.text ? " " : ""}{t.busy}…
            </span>
          )}
        </div>
      ))}
      <div ref={bottom} />

      {showSpend && <SpendSheet onClose={() => setShowSpend(false)} />}

      {/* ПОЛЕ ЗАКРЕПЛЕНО ВНИЗУ, А НЕ ЛИПКОЕ. Sticky держит элемент только пока
          тот упирается в край потока: при коротком разговоре форма оставалась
          посреди экрана, а под ней зияла пустота. Fixed ставит её над панелью
          вкладок всегда — как в мини-аппе про питание. */}
      <form onSubmit={(e) => { e.preventDefault(); void ask(text); }}
        // ОДНА СПЛОШНАЯ ПЛАШКА, а не поле и кнопка по отдельности. Раньше между
        // ними был прозрачный зазор, и закреплённая форма пропускала сквозь
        // себя текст ответа: цифры графика читались между полем и стрелкой.
        style={{ display: "flex", alignItems: "center", gap: "var(--s-2)",
                 position: "fixed", left: "var(--s-4)", right: "var(--s-4)", zIndex: 5,
                 bottom: `calc(${TAB_BAR_HEIGHT} + var(--s-2))`,
                 background: "var(--surface-2)", border: "1px solid var(--surface-3)",
                 borderRadius: "var(--r-field)", padding: "4px 4px 4px var(--s-4)",
                 boxShadow: "0 6px 20px var(--scrim)" }}>
        <input value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Спроси про свои деньги" disabled={busy}
          aria-label="Вопрос про свои деньги"
          style={{ flexGrow: 1, minWidth: 0, background: "none", border: "none",
                   padding: "10px 0", font: "inherit", fontSize: "var(--f-body)",
                   color: "var(--text)" }} />
        <button type="submit" disabled={busy || !text.trim()} aria-label="Спросить"
          style={{ width: 40, height: 40, flexShrink: 0, border: "none",
                   borderRadius: "var(--r-pill)", background: "var(--accent)",
                   color: "var(--on-accent)", font: "inherit", fontWeight: 600,
                   display: "flex", alignItems: "center", justifyContent: "center",
                   cursor: busy || !text.trim() ? "default" : "pointer",
                   opacity: busy || !text.trim() ? 0.45 : 1 }}>
          →
        </button>
      </form>
    </div>
  );
}

/** Чтение SSE. `false` — этот клиент потоков не умеет, нужен запасной путь. */
async function stream(
  question: string,
  patch: (fn: (t: Turn) => Turn) => void,
  setLeft: (v: string) => void,
): Promise<boolean> {
  const r = await fetch("/api/ask/stream", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Telegram-Init-Data": initData() },
    body: JSON.stringify({ question }),
  });
  if (!r.ok) throw new Error(String(r.status));
  if (!r.body?.getReader) return false;

  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return true;
    buf += decoder.decode(value, { stream: true });
    const blocks = buf.split("\n\n");
    buf = blocks.pop() ?? "";
    for (const block of blocks) {
      const line = block.replace(/^data: /, "").trim();
      if (!line) continue;
      let ev: { kind: string; text?: string; remaining?: string; sources?: string[] };
      try { ev = JSON.parse(line); } catch { continue; }
      if (ev.kind === "delta") {
        patch((t) => ({ ...t, text: t.text + (ev.text ?? ""), busy: null }));
      } else if (ev.kind === "reset") {
        // Модель написала преамбулу и передумала — напечатанное отменяем.
        patch((t) => ({ ...t, text: "", busy: null }));
      } else if (ev.kind === "tool") {
        patch((t) => ({ ...t, busy: SAYS[ev.text ?? ""] ?? "смотрю данные" }));
      } else if (ev.kind === "error") {
        patch((t) => ({ ...t, text: ev.text ?? "Ответ оборвался.", busy: null }));
      } else if (ev.kind === "end") {
        patch((t) => ({ ...t, text: ev.text || t.text, busy: null,
                        sources: ev.sources ?? t.sources }));
        if (ev.remaining !== undefined) setLeft(ev.remaining);
      }
    }
  }
}
